<template>
  <!-- Visual blocks (image / table / list / key takeaways / overview / form /
       sources) render as a lightweight "draft visual block" card, matching the
       design handoff. They are managed from the Structure tab, so they carry no
       per-block regenerate toolbar here — table/list get a single Regenerate
       button that opens the rich "Regenerate this section" modal. -->
  <!-- TOC: system-built (blue card), auto-synced from headings — no editing,
       no word count, no regenerate. Style/H3/label are local options that the
       server uses to rebuild the html (free). -->
  <div v-if="block.type === 'toc'" class="draft-block toc-block">
    <div class="draft-block-head">
      <span class="type-pill toc">Contents</span>
      <span class="draft-block-title">Table of contents</span>
    </div>
    <div class="draft-block-body toc-body">
      <div v-if="block.html" class="toc-render" v-html="block.html"></div>
      <div v-else class="toc-empty">Appears once the article has at least 3 sections.</div>
      <!-- Design's block-actions-row: Style pill group + H3 icon toggle. -->
      <div class="block-actions-row">
        <div class="regen-group">
          <span class="regen-group-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/></svg>
            Style
          </span>
          <button type="button" class="regen-opt" :class="{ active: tocStyle === 'numbered' }" @click="setTocOption({ tocStyle: 'numbered' })">Numbered</button>
          <button type="button" class="regen-opt" :class="{ active: tocStyle === 'bulleted' }" @click="setTocOption({ tocStyle: 'bulleted' })">Bulleted</button>
          <button type="button" class="regen-opt" :class="{ active: tocStyle === 'plain' }" @click="setTocOption({ tocStyle: 'plain' })">Plain links</button>
        </div>
        <div class="block-icons">
          <label class="toc-h3-toggle" title="Show / hide H3 sub-sections">
            <input type="checkbox" :checked="tocShowH3" @change="setTocOption({ tocShowH3: $event.target.checked })">
            Show H3s
          </label>
          <span class="toc-label-lbl">Title:</span>
          <select class="toc-label-sel" :value="tocLabel" @change="setTocOption({ tocLabel: $event.target.value })">
            <option>In this article</option>
            <option>Contents</option>
            <option>On this page</option>
          </select>
          <button class="icon-act" title="Delete" @click="$emit('delete', block)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13h10l1-13"/></svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="isVisual" class="draft-vis-block" :class="visClass">
    <div class="dvb-head">
      <span class="dvb-tag" :class="tagMeta.cls">
        <span v-if="tagMeta.icon" class="dvb-ico" v-html="tagMeta.icon"></span>{{ tagMeta.label }}
      </span>

      <!-- Image: download + generate / regenerate sit on the right of the head -->
      <button
        v-if="block.type === 'image' && assetDone"
        class="aiw2-btn btn-sm dvb-dl"
        title="Download this image"
        @click="downloadImage"
      ><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg> Download</button>
      <button
        v-if="block.type === 'image' && assetStatus !== 'generating'"
        class="aiw2-btn aiw2-btn--primary btn-sm dvb-gen"
        :disabled="imageBusy"
        @click="$emit('image', block)"
      >⚡ {{ assetDone ? 'Regenerate' : 'Generate' }} (1 credit)</button>

      <!-- Non-image visuals: optional title + status note -->
      <template v-else-if="block.type !== 'image'">
        <span v-if="visualTitle" class="dvb-title">{{ visualTitle }}</span>
        <span v-if="!canRegenVisual && visualNote" class="dvb-note">{{ visualNote }}</span>
        <button
          v-if="canRegenVisual"
          class="aiw2-btn aiw2-btn--primary btn-sm dvb-gen"
          :disabled="block.locked"
          @click="$emit('again', block)"
        >⚡ {{ block.html ? 'Regenerate' : 'Generate' }}</button>
      </template>
    </div>

    <div class="dvb-body">
      <!-- Image -->
      <template v-if="block.type === 'image'">
        <div v-if="assetDone" class="dvb-done-wrap">
          <div class="dvb-img dvb-done"><img :src="imgSrc" :alt="alt" /></div>
          <div class="dvb-alt">
            <label>Alt text <span class="dvb-alt-tag">SEO</span></label>
            <input class="dvb-alt-input" v-model="alt" @blur="$emit('alt-edit', { block, alt })" />
          </div>
          <div class="dvb-disclaimer">AI-generated image.</div>
        </div>
        <div v-else-if="assetStatus === 'generating'" class="dvb-img dvb-generating">
          <span class="dvb-spin"></span> Generating…
        </div>
        <div v-else class="dvb-img">
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/></svg>
          {{ imagePlaceholderLabel }}
          <span v-if="assetStatus === 'error'" class="dvb-img-err">Couldn't generate - try again.</span>
        </div>
      </template>

      <!-- Form is always a publish-time placeholder -->
      <div v-else-if="block.type === 'form'" class="dvb-form-ph">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/></svg>
        <span>Form placeholder - add your form embed when you publish</span>
      </div>

      <!-- List / table / takeaways / overview / sources -->
      <div v-else class="dvb-content" v-html="visualBody"></div>
    </div>
  </div>

  <!-- Text blocks (intro / H2 / H3 / FAQ) keep the full editable block + toolbar. -->
  <div v-else class="draft-block" :data-block-id="block.id" :class="[{ 'draft-h3': block.type === 'h3', 'user-edited': block.user_edited, locked: block.locked, editing }, block.status === 'error' ? 'is-error' : '']">
    <div class="draft-block-head">
      <span class="type-pill" :class="pillClass">{{ pill }}</span>
      <span v-if="titleText" class="draft-block-title">{{ titleText }}</span>
      <div class="draft-block-meta">
        <span v-if="block.user_edited" class="edited-badge">Edited by you</span>
        <span v-if="block.word_count">{{ block.word_count }} words{{ faqMeta }}</span>
      </div>
    </div>

    <div v-if="!editing" class="draft-block-body" v-html="bodyHtml" @dblclick="startEdit" title="Double-click to edit"></div>
    <div v-else class="draft-block-body editing-area">
      <textarea class="edit-textarea" rows="7" v-model="draft"></textarea>
    </div>

    <!-- Actions -->
    <div class="block-actions-row">
      <div class="regen-group">
        <span class="regen-group-label">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v6h6"/><path d="M3.51 9a9 9 0 1 0 2.13-3.36L3 9"/></svg>
          Regenerate
        </span>
        <button v-for="m in BLOCK_REGEN_MODES" :key="m.value" class="regen-opt" :disabled="block.locked" @click="onRegen(m.value)">{{ m.label }}</button>
      </div>

      <div class="block-icons">
        <button class="icon-act" title="Edit" @click="startEdit">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
        </button>
        <button class="icon-act" title="Copy" @click="copyHtml">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
        </button>
        <button class="icon-act" title="View history" @click="$emit('history', block)">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>
        </button>
        <button class="icon-act" title="Delete" @click="$emit('delete', block)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13h10l1-13"/></svg>
        </button>
        <button class="icon-act lock-icon" :class="{ locked: block.locked }" :title="block.locked ? 'Unlock' : 'Lock (protect from AI rewrites)'" @click="$emit('lock', block)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
        </button>
      </div>
    </div>

    <!-- Edit-mode actions -->
    <div v-if="editing" class="edit-mode-actions">
      <button class="aiw2-btn aiw2-btn--primary btn-sm" @click="saveEdit">Save changes</button>
      <button class="aiw2-btn btn-sm" @click="editing = false">Cancel</button>
    </div>
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import { BLOCK_REGEN_MODES, BLOCK_TYPE_PILL, IMAGE_STYLES } from "../../constants";

const PILL_CLASS = { intro: "intro", h2: "section", h3: "h3", faq: "faq" };

// Block types rendered as lightweight visual cards (no editable body / toolbar).
const VISUAL_TYPES = ["image", "table", "list", "key_highlights", "brief_overview", "form", "sources"];

// Per-visual-type badge: colour class, label and inline icon (SVG paths from the
// design handoff). Key takeaways uses the ⚡ glyph, matching the prototype.
const SVG = (paths) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
const TAG_META = {
  image: { cls: "image", label: "Image", icon: SVG('<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/>') },
  table: { cls: "table", label: "Table", icon: SVG('<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M3 10h18M9 4v16"/>') },
  list: { cls: "list", label: "List", icon: SVG('<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>') },
  form: { cls: "form", label: "Form", icon: SVG('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/>') },
  sources: { cls: "sources", label: "Sources", icon: SVG('<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>') },
  key_highlights: { cls: "takeaways", label: "⚡ Key takeaways", icon: "" },
  brief_overview: { cls: "overview", label: "Overview", icon: SVG('<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>') },
};

export default {
  name: "AiwV2BlockItem",
  props: {
    block: { type: Object, required: true },
    // When another image is generating, disable this block's image button so
    // image generations run one at a time (prevents concurrent-task corruption).
    imageBusy: { type: Boolean, default: false },
  },
  data() {
    return {
      BLOCK_REGEN_MODES,
      editing: false,
      draft: "",
      alt: (this.block.metadata && this.block.metadata.asset && this.block.metadata.asset.alt) || "",
    };
  },
  computed: {
    isVisual() {
      return VISUAL_TYPES.includes(this.block.type);
    },
    // Table/list visual blocks can be regenerated (routes through the same rich
    // "Regenerate this section" modal as prose — see DraftStep.openAgainModal).
    canRegenVisual() {
      return this.block.type === "table" || this.block.type === "list";
    },
    // TOC options (block metadata, defaults per PM spec).
    tocStyle() {
      return ((this.block.metadata || {}).toc_style) || "numbered";
    },
    tocShowH3() {
      return !!((this.block.metadata || {}).toc_show_h3);
    },
    tocLabel() {
      return ((this.block.metadata || {}).toc_label) || "In this article";
    },
    tagMeta() {
      return TAG_META[this.block.type] || { cls: "", label: BLOCK_TYPE_PILL[this.block.type] || this.block.type, icon: "" };
    },
    visClass() {
      return this.block.type === "key_highlights" ? "is-takeaways" : "";
    },
    pill() {
      return BLOCK_TYPE_PILL[this.block.type] || this.block.type;
    },
    pillClass() {
      return PILL_CLASS[this.block.type] || "";
    },
    placeholder() {
      if (this.block.type === "sources" && this.block.status !== "pending") {
        // Sources block generated but empty (research found nothing / failed):
        // explain instead of rendering a blank card.
        return "<em>No sources to cite yet — POP lists researched references here when source support finds them.</em>";
      }
      if (this.block.status === "error") {
        return "<em>Couldn't write this section — use Regenerate to retry.</em>";
      }
      return this.block.status === "pending" ? "<em>Pending…</em>" : "";
    },
    // For H2/H3/FAQ the html is "<heading>…</heading><p>…</p>…": split the
    // heading out so it can sit inline with the pill and only the body text
    // stays in the editable area (matches the design).
    headingParts() {
      if (!["h2", "h3", "faq"].includes(this.block.type)) return null;
      const html = this.block.html || "";
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const h = doc.body.querySelector("h1, h2, h3, h4");
      if (!h || h !== doc.body.firstElementChild) return null;
      const headingHtml = h.outerHTML;
      const headingText = (h.textContent || "").trim();
      h.remove();
      return { headingHtml, headingText, bodyHtml: doc.body.innerHTML };
    },
    titleText() {
      if (this.headingParts) return this.headingParts.headingText;
      return this.block.type === "faq" ? "Frequently asked questions" : "";
    },
    editableHtml() {
      return this.headingParts ? this.headingParts.bodyHtml : (this.block.html || "");
    },
    bodyHtml() {
      return this.block.html ? this.editableHtml : this.placeholder;
    },
    // FAQ meta tail — " · N Qs" when the question count is known. Prefer the
    // server count, else count the faq_generator's question blocks, else any
    // "Q." / "Q:" markers.
    faqMeta() {
      if (this.block.type !== "faq") return "";
      const meta = this.block.metadata || {};
      let n = meta.question_count || meta.questions;
      const html = this.block.html || "";
      if (!n && html) {
        const items = html.match(/class=["']faq-item["']/gi) || html.match(/class=["']question["']/gi);
        n = items ? items.length : 0;
      }
      if (!n && html) {
        const qs = html.match(/\bQ[.:]/g);
        n = qs ? qs.length : 0;
      }
      return n ? ` · ${n} Qs` : "";
    },
    // ----- visual block rendering -----
    visualParts() {
      const html = this.block.html || "";
      if (!html) return { title: "", body: "" };
      const doc = new DOMParser().parseFromString(html, "text/html");
      const h = doc.body.querySelector("h1, h2, h3, h4");
      let title = "";
      if (h && h === doc.body.firstElementChild) {
        title = (h.textContent || "").trim();
        h.remove();
      }
      return { title, body: doc.body.innerHTML };
    },
    visualTitle() {
      if (this.block.type === "sources") return "References";
      return this.visualParts.title;
    },
    visualBody() {
      const raw = this.visualParts.body || this.block.html || "";
      if (this.block.type === "list" || this.block.type === "sources") {
        // Design: the box shows only the list; its title lives in the head.
        // Drops the list's lead/wrap paragraphs and the sources section + its
        // inner "References" heading (now shown beside the tag).
        const doc = new DOMParser().parseFromString(raw, "text/html");
        const list = doc.querySelector("ol, ul");
        if (list) return list.outerHTML;
      }
      return raw;
    },
    visualNote() {
      if (this.block.type === "key_highlights") return "Generated with your draft";
      if (this.block.type === "form") return "Placeholder";
      if (this.block.type === "sources") return this.block.html ? "" : "Placeholder";
      return this.block.html ? "Generated" : "Placeholder";
    },
    imageStyleLabel() {
      const meta = this.block.metadata || {};
      const style = (meta.asset && meta.asset.image_style) || meta.image_style || "";
      const found = IMAGE_STYLES.find((s) => s.value === style);
      return found ? found.label : "";
    },
    imagePlaceholderLabel() {
      return this.imageStyleLabel ? `Image placeholder · ${this.imageStyleLabel}` : "Image placeholder";
    },
    assetDone() {
      const a = this.block.metadata && this.block.metadata.asset;
      return a && a.status === "done" && a.filename;
    },
    imgSrc() {
      const a = this.block.metadata && this.block.metadata.asset;
      // Build against the API origin (API_URL), matching the legacy writer. A
      // relative "/api/..." resolves to the frontend origin and 404s in dev.
      return a && a.filename ? `${API_URL}/ai-run/images/${a.filename}` : "";
    },
    assetStatus() {
      const a = this.block.metadata && this.block.metadata.asset;
      return (a && a.status) || "";
    },
  },
  watch: {
    "block.metadata.asset.alt"(v) {
      if (!this.editing) this.alt = v || "";
    },
  },
  methods: {
    async downloadImage() {
      const a = (this.block.metadata && this.block.metadata.asset) || {};
      try {
        const res = await fetch(this.imgSrc);
        if (!res.ok) throw new Error("HTTP " + res.status);
        saveAs(await res.blob(), a.filename || "image.png");
      } catch (e) {
        // Fallback: open the image directly so the user can still save it.
        window.open(this.imgSrc, "_blank");
      }
    },
    startEdit() {
      if (this.block.locked) return;
      // Edit only the paragraph body; the heading is managed in the Structure tab.
      this.draft = this.editableHtml;
      this.editing = true;
    },
    saveEdit() {
      this.editing = false;
      const heading = this.headingParts ? this.headingParts.headingHtml : "";
      this.$emit("edit", { block: this.block, html: heading + this.draft });
    },
    setTocOption(opts) {
      this.$emit("toc-options", { block: this.block, ...opts });
    },
    onRegen(mode) {
      if (mode === "again") this.$emit("again", this.block);
      else this.$emit("regenerate", { block: this.block, mode });
    },
    copyHtml() {
      if (navigator.clipboard) navigator.clipboard.writeText(this.block.html || "");
    },
  },
};
</script>

<style scoped>
.draft-block { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 20px; margin-bottom: 10px; transition: all .15s; }
.draft-block:hover { border-color: var(--border-strong, #ccc); }
.draft-block.locked { background: linear-gradient(90deg, #fffaf0 0%, #fff 25%); }
.draft-block.user-edited { background: linear-gradient(90deg, var(--pop-orange-soft, #ffeddd) 0%, #fff 25%); }
.draft-block.draft-h3 { margin-left: 26px; border-left: 2px solid var(--border, #e4e4e4); }
.draft-block.is-error { border-color: var(--pop-red, #ff3939); }

.draft-block-head { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.type-pill { font-size: 9.5px; font-weight: 700; letter-spacing: .04em; padding: 3px 8px; border-radius: 4px; text-transform: uppercase; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); }
.type-pill.intro { background: var(--pop-purple-soft, #e3eeef); color: var(--pop-purple, #014751); }
.type-pill.section { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); }
.type-pill.h3 { background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); border: 1px solid var(--border, #e4e4e4); }
.type-pill.faq { background: var(--pop-blue-soft, #c7ddff); color: var(--pop-blue, #316bff); }
.draft-block-title { flex: 1; font-size: 15.5px; color: var(--text, #052e33); font-weight: 700; line-height: 1.3; min-width: 0; }
.draft-block-meta { font-size: 11px; color: var(--text-3, #6b7b7e); margin-left: auto; display: flex; gap: 10px; align-items: center; flex-shrink: 0; }
.edited-badge { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); font-size: 9px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; padding: 3px 8px; border-radius: 4px; }

.draft-block-body { font-size: 13.5px; color: var(--text-2, #014751); line-height: 1.65; cursor: text; }
.draft-block-body:hover { background: linear-gradient(0deg, rgba(255,87,0,.03), rgba(255,87,0,.03)); border-radius: 6px; }
.draft-block-body :deep(p) { margin: 0 0 6px; color: var(--text-2, #014751); font-size: 13.5px; line-height: 1.65; }
.draft-block-body :deep(p:last-child) { margin-bottom: 0; }
.draft-block-body :deep(h2) { font-size: 15.5px; font-weight: 700; color: var(--text, #052e33); margin: 0 0 6px; line-height: 1.3; }
.draft-block-body :deep(h3) { font-size: 14px; font-weight: 700; color: var(--text, #052e33); margin: 8px 0 4px; }
.draft-block-body :deep(ul), .draft-block-body :deep(ol) { margin: 0; padding-left: 20px; line-height: 1.7; }
.draft-block-body :deep(table) { border-collapse: collapse; width: 100%; margin-top: 4px; }
.draft-block-body :deep(td), .draft-block-body :deep(th) { border: 1px solid var(--border, #e4e4e4); padding: 6px 8px; font-size: 12.5px; }
.draft-block-body :deep(.internal-link) { color: var(--pop-blue, #316bff); font-weight: 600; }
/* FAQ items (faq_generator markup) — design's bold "Q." question + plain answer */
.draft-block-body :deep(.faq-item) { margin: 0 0 14px; }
.draft-block-body :deep(.faq-item:last-child) { margin-bottom: 0; }
.draft-block-body :deep(.faq-item .question) { margin: 0; }
.draft-block-body :deep(.faq-item .question h4) { font-size: 13.5px; font-weight: 700; color: var(--text-2, #014751); margin: 0 0 4px; line-height: 1.4; }
.draft-block-body :deep(.faq-item .question h4)::before { content: "Q. "; }
.draft-block-body :deep(.faq-item .answer p) { margin: 0 0 6px; color: var(--text-2, #014751); font-size: 13.5px; line-height: 1.65; }
.draft-block-body :deep(.faq-item .answer p:last-child) { margin-bottom: 0; }
/* Sources / References block (citation_block_generator) — matches design handoff */
.draft-block-body :deep(.article-sources) { margin: 4px 0 0; }
.draft-block-body :deep(.article-sources h2) { font-size: 15px; font-weight: 700; color: var(--text, #052e33); margin: 0 0 6px; }
.draft-block-body :deep(.dvb-sources) { margin: 0; padding-left: 20px; font-size: 12.5px; line-height: 1.7; }
.draft-block-body :deep(.dvb-sources a) { color: var(--pop-blue, #316bff); text-decoration: underline; }
.draft-block-body :deep(.dvb-url-status) { font-size: 11px; font-weight: 600; color: var(--text-3, #6b7b7e); margin-left: 4px; }
.draft-block-body :deep(.dvb-url-status.ok) { color: #1c805d; }
.editing-area { cursor: auto; }
.edit-textarea { width: 100%; box-sizing: border-box; min-height: 160px; resize: vertical; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); line-height: 1.6; border: 1px solid var(--pop-orange, #ff5700); border-radius: 8px; padding: 10px 12px; outline: none; box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }

/* ---- TOC draft block (design: blue system card, auto-synced) ---- */
.draft-block.toc-block { border-color: var(--pop-blue, #316bff); box-shadow: 0 0 0 3px var(--pop-blue-tint, #e9f0ff); }
.type-pill.toc { background: var(--pop-blue-tint, #e9f0ff); color: var(--pop-blue, #316bff); }
.toc-body { cursor: default; }
.toc-empty { font-size: 12.5px; color: var(--text-3, #6b7b7e); font-style: italic; }
/* Rendered TOC (design .toc-render) — inner markup is nav.pop-toc via v-html */
.toc-render { border: 1px solid var(--border, #e4e4e4); border-radius: 8px; background: var(--surface-2, #f7f7f7); padding: 16px 18px; }
.toc-render ::v-deep nav.pop-toc h2 { font-size: 14px; font-weight: 800; color: var(--text-2, #014751); margin: 0 0 12px; text-transform: uppercase; letter-spacing: .05em; display: flex; align-items: center; gap: 8px; }
/* Orange list icon before the label (design .toc-render-title svg) — the title
   comes from server html, so the icon is painted via a mask. */
.toc-render ::v-deep nav.pop-toc h2::before { content: ""; flex: none; width: 15px; height: 15px; background-color: var(--pop-orange, #ff5700); -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>') center / 15px 15px no-repeat; mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>') center / 15px 15px no-repeat; }
.toc-render ::v-deep .toc-list { list-style: none; margin: 0; padding: 0; counter-reset: toc; }
.toc-render ::v-deep .toc-list > li { counter-increment: toc; margin: 0 0 9px; }
.toc-render ::v-deep .toc-list > li:last-child { margin-bottom: 0; }
.toc-render ::v-deep .toc-list a { display: inline-flex; gap: 10px; color: var(--text-2, #014751); font-weight: 600; font-size: 14px; text-decoration: none; }
.toc-render ::v-deep ol.toc-list > li > a::before { content: counter(toc) "."; color: var(--pop-orange, #ff5700); font-weight: 800; min-width: 20px; }
.toc-render ::v-deep ul.toc-list > li > a::before { content: "•"; color: var(--pop-orange, #ff5700); font-weight: 800; min-width: 14px; }
.toc-render ::v-deep ul.toc-plain > li > a::before { content: none; }
.toc-render ::v-deep .toc-sub { list-style: none; margin: 7px 0 2px 30px; padding: 0; }
.toc-render ::v-deep .toc-sub > li { margin: 0 0 6px; }
.toc-render ::v-deep .toc-sub a { color: var(--text-3, #6b7b7e); font-weight: 500; font-size: 13px; text-decoration: none; display: inline-flex; gap: 8px; }
.toc-render ::v-deep .toc-sub a::before { content: "\2013"; color: var(--text-muted, #9aa6a8); min-width: 14px; }
/* Style options use the standard block-actions-row/.regen-opt toolbar (design);
   only the selected-state + H3-toggle-on modifiers are TOC-specific. */
.regen-opt.active { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange, #ff5700); }
.toc-h3-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--text-2, #014751); cursor: pointer; margin-right: 12px; }
.toc-label-lbl { font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751); margin-right: 6px; }
.toc-label-sel { font-family: inherit; font-size: 12.5px; color: var(--text-2, #014751); border: 1px solid var(--border, #e4e4e4); border-radius: 7px; padding: 4px 8px; background: #fff; }

/* Actions row */
.block-actions-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border, #e4e4e4); }
.regen-group { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 999px; padding: 4px 6px 4px 12px; }
.regen-group-label { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 800; color: var(--text-2, #014751); margin-right: 2px; }
.regen-group-label svg { width: 14px; height: 14px; color: var(--pop-orange, #ff5700); }
.regen-opt { background: #fff; border: 1px solid var(--border, #e4e4e4); color: var(--text-2, #014751); font-family: inherit; font-size: 12px; font-weight: 600; white-space: nowrap; padding: 5px 11px; border-radius: 999px; cursor: pointer; transition: background .12s, color .12s, border-color .12s; }
.regen-opt:hover:not(:disabled) { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); border-color: var(--border-strong, #ccc); }
.regen-opt:disabled { opacity: .5; cursor: not-allowed; }
.block-icons { display: inline-flex; align-items: center; gap: 2px; margin-left: auto; }
.icon-act { width: 32px; height: 32px; border: none; background: transparent; border-radius: 7px; color: var(--text-3, #6b7b7e); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: background .12s, color .12s; }
.icon-act svg { width: 17px; height: 17px; }
.icon-act:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }
.icon-act.lock-icon.locked { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); }

.edit-mode-actions { display: flex; gap: 8px; margin-top: 12px; }
.btn-sm { padding: 7px 14px; font-size: 0.82rem; }

/* ===== Draft visual block (image / table / list / takeaways / form / sources) ===== */
.draft-vis-block { border: 1px solid var(--border, #e4e4e4); background: #fff; border-radius: var(--r-lg, 10px); padding: 14px 16px; margin: 0 0 10px; }
.draft-vis-block.is-takeaways { background: linear-gradient(135deg, #FFF7DB 0%, #FDEFC9 55%, #FCE7D6 100%); border-color: rgba(217,178,90,.4); }
.dvb-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.dvb-tag { font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 6px; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); color: var(--text-2, #014751); display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.dvb-tag .dvb-ico { display: inline-flex; }
.dvb-tag :deep(svg) { width: 14px; height: 14px; display: block; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.dvb-tag.image { color: #2e7d9a; border-color: #bfe0ea; }
.dvb-tag.table { color: #7a5aa8; border-color: #ddd0ee; margin-bottom: 0; }
.dvb-tag.list { color: #1f8a5b; border-color: #c7e9d6; }
.dvb-tag.form { color: #2e7d9a; border-color: #bfe0ea; }
.dvb-tag.sources, .dvb-tag.overview { color: #7a5aa8; border-color: #ddd0ee; }
.dvb-tag.takeaways { color: #92400e; background: var(--pop-amber-soft, #faf3d0); border-color: #fcd681; }
.dvb-title { font-size: 13.5px; font-weight: 700; color: var(--text, #052e33); }
.dvb-note { font-size: 11.5px; color: var(--text-3, #6b7b7e); margin-left: auto; }
.dvb-gen { margin-left: auto; }
/* Download sits left of Generate/Regenerate; it takes over the auto margin so
   the pair stays right-aligned with the download first. */
.dvb-dl { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; }
.dvb-dl svg { width: 13px; height: 13px; }
.dvb-dl + .dvb-gen { margin-left: 8px; }
.dvb-body { margin-top: 12px; }

.dvb-img { min-height: 150px; border-radius: 8px; border: 1.5px dashed var(--border-strong, #ccc); background: var(--surface-2, #f7f7f7); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: var(--text-3, #6b7b7e); font-size: 12px; font-weight: 600; text-align: center; padding: 12px; }
.dvb-img svg { width: 30px; height: 30px; fill: none; stroke: currentColor; stroke-width: 1.7; }
.dvb-img.dvb-generating { color: var(--pop-orange-deep, #b23d00); }
.dvb-img.dvb-done { border-style: solid; padding: 0; overflow: hidden; min-height: 0; }
.dvb-img.dvb-done img { width: 100%; display: block; border-radius: 6px; }
.dvb-img-err { color: var(--pop-red, #ff3939); font-weight: 600; }
.dvb-spin { width: 26px; height: 26px; border: 3px solid var(--pop-orange-tint, #ffd9c2); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; animation: dvbspin .8s linear infinite; }
@keyframes dvbspin { to { transform: rotate(360deg); } }
.dvb-alt { margin-top: 12px; display: flex; flex-direction: column; gap: 5px; }
.dvb-alt label { font-size: 11px; font-weight: 700; color: var(--text-2, #014751); display: flex; align-items: center; gap: 7px; }
.dvb-alt-tag { font-size: 9px; font-weight: 800; letter-spacing: .03em; background: var(--pop-blue-soft, #c7ddff); color: var(--pop-blue, #316bff); padding: 2px 6px; border-radius: 4px; }
.dvb-alt-input { font-family: inherit; font-size: 12.5px; color: var(--text, #052e33); border: 1px solid var(--border, #e4e4e4); border-radius: 7px; padding: 8px 11px; background: #fff; width: 100%; box-sizing: border-box; }
.dvb-alt-input:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.dvb-disclaimer { font-size: 11px; line-height: 1.5; color: var(--text-3, #6b7b7e); margin: 8px 0 0; padding: 9px 11px; background: var(--surface-2, #f7f7f7); border-radius: 7px; }

.dvb-form-ph { display: flex; align-items: center; gap: 10px; padding: 22px 18px; border: 1.5px dashed var(--border-strong, #ccc); border-radius: 8px; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); font-size: 12.5px; font-weight: 600; }
.dvb-form-ph svg { width: 22px; height: 22px; flex: none; }

/* Generated visual content (rendered from block.html) */
.dvb-content :deep(ul), .dvb-content :deep(ol) { margin: 0; padding-left: 20px; font-size: 13.5px; color: var(--text-2, #014751); line-height: 1.7; }
/* The app's global reset strips list markers — restore them for visual lists. */
.dvb-content :deep(ul) { list-style: disc outside; padding-left: 22px; }
.dvb-content :deep(ol) { list-style: decimal outside; padding-left: 22px; }
/* References (sources) list: numbered, smaller than regular list content. */
.dvb-content :deep(.dvb-sources) { font-size: 12.5px; color: var(--text-2, #014751); line-height: 1.7; }
.dvb-content :deep(.dvb-sources a) { color: var(--pop-blue, #316bff); text-decoration: underline; }
.dvb-content :deep(li) { display: list-item; margin: 0 0 6px; padding-left: 4px; }
.dvb-content :deep(li:last-child) { margin-bottom: 0; }
/* No outer border; the shaded header keeps rounded top corners (radius + clip). */
.dvb-content :deep(table) { width: 100%; border-collapse: collapse; font-size: 12.5px; border-radius: 6px; overflow: hidden; }
.dvb-content :deep(th) { background: var(--surface-2, #f7f7f7); text-align: left; padding: 8px 10px; font-weight: 700; color: var(--text-2, #014751); border-bottom: 1px solid var(--border, #e4e4e4); }
.dvb-content :deep(th:first-child) { border-top-left-radius: 6px; }
.dvb-content :deep(th:last-child) { border-top-right-radius: 6px; }
.dvb-content :deep(td) { padding: 8px 10px; border-bottom: 1px solid var(--border, #e4e4e4); color: var(--text-2, #014751); }
.dvb-content :deep(tr:last-child td) { border-bottom: none; }
.dvb-content :deep(a) { color: var(--pop-blue, #316bff); text-decoration: none; }
.dvb-content :deep(a:hover) { text-decoration: underline; }
.dvb-content :deep(p) { margin: 0 0 6px; font-size: 13.5px; color: var(--text-2, #014751); line-height: 1.65; }
.dvb-content :deep(p:last-child) { margin-bottom: 0; }
</style>
